import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';

import OrganisationSvg from 'common/images/OrganisationSvg';
import { canAccessOrgDashboard } from 'common/rbac/util';
import { userActions } from '../../onboarding/user.actions';
import { orgContextActions } from './orgContext';
import { getOrgDashboardPath } from './orgRouteUtils';
import { history } from '../../common/helpers';
import { buildOrgSiteTree } from './utils/orgSiteTree';
import OrgSiteBreadcrumb from '../Menu/OrgSiteBreadcrumb';
import {
  PanelContainer,
  ScrollArea,
  SectionTitle,
  EmptyMsg,
  TreeRoot,
  TreeSiteRow
} from './OrgSiteHierarchy.styled';

function buildFlatItems(accounts) {
  return (accounts || [])
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map(account => ({ type: 'flat', text: account.name, value: account.id }));
}

export class OrgSiteHierarchy extends React.Component {
  getItems = () => {
    const user = this.props.user && this.props.user.user ? this.props.user.user : null;
    if (!user) return [];
    const switcher = user.account_switcher;
    if (switcher && Array.isArray(switcher.items) && switcher.items.length > 0) {
      return switcher.items;
    }
    return buildFlatItems(user.accounts);
  };

  resolveActiveOrg = (tree, activeOrgId) => {
    if (activeOrgId == null) return null;

    const fromTree = tree.orgs.find(org => org.orgId === activeOrgId);
    if (fromTree) return fromTree;

    const { orgContext, t } = this.props;
    const orgName = (orgContext && orgContext.id === activeOrgId && orgContext.name)
      ? orgContext.name
      : t('orgView.breadcrumb.unknownOrg');

    // Switcher may omit an org the user can still open (UOA / sparse associations).
    // Rebuild site rows from accounts that carry this organisation_id.
    const user = this.props.user && this.props.user.user ? this.props.user.user : null;
    const sites = (user && user.accounts ? user.accounts : [])
      .filter(account => Number(account.organisation_id) === Number(activeOrgId))
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(account => ({ id: account.id, name: account.name }));

    return {
      orgId: activeOrgId,
      orgName,
      sites
    };
  };

  getSelectedSiteId = () => {
    const user = this.props.user && this.props.user.user ? this.props.user.user : null;
    return user ? user.account_id : null;
  };

  handleOrgClick = (org) => {
    if (org.orgId == null) return;
    const user = this.props.user && this.props.user.user;
    if (!canAccessOrgDashboard(user, org.orgId)) return;
    this.props.setOrgContext({ id: org.orgId, name: org.orgName });
    history.push(getOrgDashboardPath(org.orgId));
  };

  handleSiteClick = (siteId) => {
    // Same site: drop org context only, no account switch (avoids a store reset).
    if (siteId === this.getSelectedSiteId()) {
      this.props.clearOrgContext();
      history.push('/');
      return;
    }
    // selectAccount() resets the store (incl. orgContext) and redirects to '/'
    // itself — pushing here too would double-navigate.
    this.props.selectAccount(siteId);
  };

  renderOrgRoot = (org, { selected = false, clickable = false } = {}) => (
    <TreeRoot
      data-test="org-header"
      selected={selected}
      clickable={clickable}
      onClick={clickable ? () => this.handleOrgClick(org) : undefined}
    >
      <span className="icon">
        <OrganisationSvg fill={this.props.primaryColor || '#00A651'} width={16} height={15} />
      </span>
      <span className="name">{org.orgName}</span>
    </TreeRoot>
  );

  renderSites = (sites, { allowSelected = true } = {}) => {
    const { t } = this.props;
    // On an org dashboard no site is the active selection — account_id is just
    // the last site visited and must not paint a row green.
    const selectedSiteId = allowSelected ? this.getSelectedSiteId() : null;
    return sites.map((site, idx) => {
      const hasCount = site.machineCount != null;
      return (
        <TreeSiteRow
          key={`site-${site.id}`}
          data-test="site-row"
          className={idx === sites.length - 1 ? 'last-site' : ''}
          selected={selectedSiteId === site.id}
          onClick={() => this.handleSiteClick(site.id)}
        >
          <span className="text">{site.name}</span>
          {hasCount && (
            <span className="count">
              {t('orgView.machineCountAbbrev', { defaultValue: '{{count}} mc', count: site.machineCount })}
            </span>
          )}
        </TreeSiteRow>
      );
    });
  };

  render() {
    const { t, activeOrgId } = this.props;
    const { orgs, flatSites } = buildOrgSiteTree(this.getItems());
    const tree = { orgs, flatSites };

    if (orgs.length === 0 && flatSites.length === 0) {
      return (
        <PanelContainer>
          <EmptyMsg>{t('orgView.noOrganisations', 'No organisations available')}</EmptyMsg>
        </PanelContainer>
      );
    }

    // On an org dashboard: org as tree root (factory icon), sites as dotted children.
    const activeOrg = this.resolveActiveOrg(tree, activeOrgId);

    if (activeOrg) {
      return (
        <PanelContainer>
          <OrgSiteBreadcrumb activeOrgId={activeOrgId} />
          <ScrollArea>
            {this.renderOrgRoot(activeOrg, { selected: true })}
            {activeOrg.sites.length === 0
              ? <EmptyMsg>{t('orgView.noSitesInOrg', 'No sites in this organisation')}</EmptyMsg>
              : this.renderSites(activeOrg.sites, { allowSelected: false })}
          </ScrollArea>
        </PanelContainer>
      );
    }

    // Fallback (no resolvable active org): full org → site tree.
    const user = this.props.user && this.props.user.user;
    return (
      <PanelContainer>
        <OrgSiteBreadcrumb activeOrgId={activeOrgId} />
        <ScrollArea>
          {orgs.map(org => (
            <React.Fragment key={`org-${org.orgId}`}>
              {this.renderOrgRoot(org, {
                selected: activeOrgId != null && org.orgId === activeOrgId,
                clickable: org.orgId != null && canAccessOrgDashboard(user, org.orgId)
              })}
              {this.renderSites(org.sites)}
            </React.Fragment>
          ))}
          {flatSites.length > 0 && orgs.length > 0 && (
            <SectionTitle>{t('orgView.otherSites', 'Other sites')}</SectionTitle>
          )}
          {flatSites.length > 0 && this.renderSites(flatSites)}
        </ScrollArea>
      </PanelContainer>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  orgContext: state.orgContext,
  primaryColor: state.companyReducer
    && state.companyReducer.partner
    && state.companyReducer.partner.theme
    && state.companyReducer.partner.theme.primaryColor
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      selectAccount: userActions.selectAccount,
      setOrgContext: orgContextActions.setOrgContext,
      clearOrgContext: orgContextActions.clearOrgContext
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(OrgSiteHierarchy));
